import type { Observable } from 'rxjs';
import {
  takeUntil,
  share,
  mergeMap,
  shareReplay,
  distinctUntilChanged,
  from,
  mergeAll,
  toArray,
  map,
  filter,
  merge,
  iif,
  of,
  bufferCount,
  last,
  catchError,
  EMPTY,
  scan,
  reduce,
  finalize,
} from 'rxjs';
import { cloneDeep, uniq } from 'lodash';

import { useQueryParamsStore } from '@apps/main/new-desktop/core/stores';

import {
  isHotelIngredientResponse,
  isFlightIngredientResponse,
  isFerryIngredientResponse,
  isAvailableHotelIngredientResponse,
  isOwnStayIngredientResponse,
  isAvailableActivityIngredientResponse,
  isTransportIngredientResponse,
} from '@libs/search-service-smart-search';
import {
  IngredientType,
  AvailabilityOrder,
} from '@libs/trip-planner-interfaces';
import type {
  HotelIngredientAdapter,
  ItineraryWithPrice,
  OverviewStaticInfoResult,
  ItinerarySearchCriteria,
  ItineraryResponse,
  ItineraryMainIngredientResponse,
  HeartedHotel,
  ActivityIngredientAdapter,
  IngredientId,
  OwnStayIngredientAdapter,
  HotelIngredientResponse,
  OwnStayIngredientResponse,
  Money,
  ItinerarySuggestionPrices,
} from '@libs/trip-planner-interfaces';
import { SemboDate } from '@libs/sembo-dates';
import {
  isDefined,
  logged,
  ingredientDateComparer,
  retryHandled,
} from '@libs/utils';
import {
  trackItineraryHotelStaticInfoRetriesStarted,
  trackItineraryHotelStaticInfoRetriesSucceeded,
} from '@libs/feature-toggle';

import { logException } from '../plugins/logException';
import type { SmartSearchService } from '../search/smartSearchService';
import {
  isHotelIngredientAdapter,
  isOwnStayIngredientAdapter,
} from '../recipe/ingredients';

import type { OverviewStaticInfoCache } from './models/overviewStaticInfoCache';

type IngredientProductCodes = {
  hotelCodes: string[];
  activityCodes: string[];
};

type HeartedHotelsByIngredient = Map<IngredientId, HeartedHotel[]>;
type ActivitiesByIngredient = Map<IngredientId, ActivityIngredientAdapter[]>;
type ProductCodesByIngredient = Map<IngredientId, IngredientProductCodes>;

export class ItinerarySearchResults {
  public readonly itineraries: Observable<ItineraryWithPrice>;
  public readonly cheapestItinerary: Observable<ItineraryWithPrice>;
  public readonly staticInfo: Observable<OverviewStaticInfoResult>;
  public readonly mostPopularItinerarySuggestions: Observable<ItinerarySuggestionPrices>;
  public readonly cheapestItinerarySuggestions: Observable<ItinerarySuggestionPrices>;
  public readonly mostPopularItinerarySuggestionPrice: Observable<Money | null>;
  public readonly cheapestItinerarySuggestionPrice: Observable<Money | null>;
  private uniqueProductCodesByIngredient = new Map<IngredientId, Set<string>>();

  constructor(
    searchService: SmartSearchService,
    public criteria: ItinerarySearchCriteria,
    cancel: Observable<unknown>,
    private cache: OverviewStaticInfoCache
  ) {
    // Keep the caller's criteria immutable: ViewModels use it to decide
    // whether an arriving result still belongs to the current search.
    const priceAccuracyToken =
      criteria.priceAccuracyToken ?? useQueryParamsStore().queryParams?.pat;
    const requestCriteria =
      priceAccuracyToken === undefined
        ? criteria
        : { ...criteria, priceAccuracyToken };

    criteria.userCriteria.ingredients?.forEach((ingredient) =>
      this.uniqueProductCodesByIngredient.set(
        ingredient.ingredientId,
        new Set()
      )
    );

    const searchResults = searchService
      .searchItineraries(requestCriteria)
      .pipe(takeUntil(cancel), share());

    this.itineraries = searchResults.pipe(
      mergeMap((itineraryResponse) => this.flattenItinerary(itineraryResponse)),
      logged('itineraries'),
      shareReplay()
    );

    const itinerarySuggestions = this.itineraries.pipe(
      last(),
      catchError(() => EMPTY),
      map(() => searchService.searchItinerarySuggestions(requestCriteria)),
      share()
    );

    this.mostPopularItinerarySuggestions = itinerarySuggestions.pipe(
      mergeMap((suggestions) => suggestions.mostPopularSuggestedItineraries)
    );

    this.mostPopularItinerarySuggestionPrice = this.getCheapestItinerary(
      this.mostPopularItinerarySuggestions
    ).pipe(
      map(
        (mostPopularItinerarySuggestion) =>
          mostPopularItinerarySuggestion.totalPrice ??
          mostPopularItinerarySuggestion.totalPriceWithAlternative
      )
    );

    this.cheapestItinerarySuggestions = itinerarySuggestions.pipe(
      mergeMap((suggestions) => suggestions.cheapestSuggestedItineraries)
    );

    this.cheapestItinerarySuggestionPrice = this.getCheapestItinerary(
      this.cheapestItinerarySuggestions
    ).pipe(
      map(
        (mostPopularItinerarySuggestion) =>
          mostPopularItinerarySuggestion.totalPrice ??
          mostPopularItinerarySuggestion.totalPriceWithAlternative
      )
    );

    this.cheapestItinerary = this.getCheapestItinerary(this.itineraries);
    this.staticInfo = this.getStaticInfo(requestCriteria, searchService);
  }

  private flattenItinerary({
    key,
    date,
    itinerary,
    prices,
    isSearched,
    isSuggested,
  }: ItineraryResponse): Observable<ItineraryWithPrice> {
    return from(itinerary).pipe(
      mergeAll(),
      toArray(),
      map<ItineraryMainIngredientResponse[], ItineraryWithPrice>(
        (ingredients) => {
          const itinerary = cloneDeep(ingredients);

          itinerary.map((ingredient) => {
            if (
              isHotelIngredientResponse(ingredient) ||
              isOwnStayIngredientResponse(ingredient)
            ) {
              ingredient.subIngredientResponse.sort((a, b) =>
                ingredientDateComparer(a, b, isTransportIngredientResponse)
              );
            }

            return ingredient;
          });

          const ownStayOnlyItinerary = itinerary.filter(
            isOwnStayIngredientResponse
          );
          const hotelOnlyItinerary = itinerary.filter(
            isHotelIngredientResponse
          );
          const flightOnlyItinerary = itinerary.filter(
            isFlightIngredientResponse
          );
          const ferryOnlyItinerary = itinerary.filter(
            isFerryIngredientResponse
          );

          const isPartial = itinerary.some((response) => {
            if (
              isAvailableHotelIngredientResponse(response) ||
              isOwnStayIngredientResponse(response)
            ) {
              return response.subIngredientResponse.some(
                (subIngredient) => !subIngredient.isAvailable
              );
            }

            return !response.isAvailable;
          });

          const isOnlyOwnStay = itinerary.every(
            (response) =>
              isOwnStayIngredientResponse(response) &&
              !response.subIngredientResponse.length
          );

          return {
            key,
            date,
            totalPrice: isPartial || isOnlyOwnStay ? null : prices.totalPrice,
            totalPriceWithAlternative: prices.totalPriceWithAlternative,
            grossPrice: prices.grossPrice,
            grossPriceWithAlternative: prices.grossPriceWithAlternative,
            pricePerPerson: prices.pricePerPerson,
            grossPricePerPerson: prices.grossPricePerPerson,
            pricePerPersonWithAlternative: prices.pricePerPersonWithAlternative,
            grossPriceWithAlternativePerPerson:
              prices.grossPriceWithAlternativePerPerson,
            cheapestPrice: null,
            mostPopularPrice: null,
            itinerary,
            ownStayOnlyItinerary,
            hotelOnlyItinerary,
            flightOnlyItinerary,
            ferryOnlyItinerary,
            isSearched,
            isSuggested,
          };
        }
      ),
      filter(
        (itineraryWithPrice) =>
          itineraryWithPrice.itinerary.length === itinerary.length
      )
    );
  }

  private getCheapestItinerary(
    itineraries: Observable<ItineraryWithPrice>
  ): Observable<ItineraryWithPrice>;
  private getCheapestItinerary(
    itineraries: Observable<ItinerarySuggestionPrices>
  ): Observable<ItinerarySuggestionPrices>;
  private getCheapestItinerary(
    itineraries: Observable<ItineraryWithPrice | ItinerarySuggestionPrices>
  ): Observable<ItineraryWithPrice | ItinerarySuggestionPrices> {
    const cheapestCompleteItinerary$ = itineraries.pipe(
      filter((itinerary) => !!itinerary.totalPrice),
      scan((previousItinerary, currentItinerary) =>
        currentItinerary.isSuggested ? currentItinerary : previousItinerary
      ),
      distinctUntilChanged((previousItinerary, currentItinerary) => {
        const isSameAmount =
          previousItinerary.totalPrice?.amount ===
          currentItinerary.totalPrice?.amount;
        const isSameDate = new SemboDate(previousItinerary.date).isSame(
          currentItinerary.date
        );
        return isSameAmount && isSameDate;
      }),
      shareReplay()
    );

    const cheapestAlternativeItinerary$ = itineraries.pipe(
      filter((itinerary) => !!itinerary.totalPriceWithAlternative),
      reduce((previousItinerary, currentItinerary) =>
        currentItinerary.isSuggested ? currentItinerary : previousItinerary
      ),
      distinctUntilChanged((previousItinerary, currentItinerary) => {
        const isSameAmount =
          previousItinerary.totalPriceWithAlternative?.amount ===
          currentItinerary.totalPriceWithAlternative?.amount;
        const isSameDate = new SemboDate(previousItinerary.date).isSame(
          currentItinerary.date
        );
        return isSameAmount && isSameDate;
      }),
      shareReplay()
    );

    const firstUnavailableItinerary$ = itineraries.pipe(
      filter(
        (itinerary) =>
          !itinerary.totalPrice && !itinerary.totalPriceWithAlternative
      ),
      reduce((previousItinerary, currentItinerary) =>
        currentItinerary.isSuggested ? currentItinerary : previousItinerary
      ),
      distinctUntilChanged((previousItinerary, currentItinerary) =>
        new SemboDate(previousItinerary.date).isSame(currentItinerary.date)
      )
    );

    return merge(
      cheapestCompleteItinerary$,
      cheapestAlternativeItinerary$.pipe(takeUntil(cheapestCompleteItinerary$)),
      firstUnavailableItinerary$.pipe(
        takeUntil(
          merge(cheapestCompleteItinerary$, cheapestAlternativeItinerary$)
        )
      )
    );
  }

  private getStaticInfo(
    criteria: ItinerarySearchCriteria,
    searchService: SmartSearchService
  ): Observable<OverviewStaticInfoResult> {
    const hotelOnlyIngredients =
      criteria.userCriteria.ingredients?.filter(isHotelIngredientAdapter) ?? [];
    const ownStayOnlyIngredients =
      criteria.userCriteria.ingredients?.filter(isOwnStayIngredientAdapter) ??
      [];

    const recipeProductCodesStaticInfoAdapters: HotelIngredientAdapter[] =
      this.getRecipeProductCodesStaticInfoAdapters(
        hotelOnlyIngredients,
        ownStayOnlyIngredients
      );

    const relevantItineraryStaticInfoAdapters: Observable<
      HotelIngredientAdapter[]
    > = this.getRelevantItineraryStaticInfoAdapters(
      [...hotelOnlyIngredients, ...ownStayOnlyIngredients],
      criteria.userCriteria.selectedDate
    );

    const remainingItinerariesStaticInfoAdapters: Observable<
      HotelIngredientAdapter[]
    > = this.getItinerariesStaticInfoAdapters(this.itineraries, [
      ...hotelOnlyIngredients,
      ...ownStayOnlyIngredients,
    ]);

    const staticInfoAdapters: Observable<HotelIngredientAdapter[]> = merge(
      of(recipeProductCodesStaticInfoAdapters),
      relevantItineraryStaticInfoAdapters,
      remainingItinerariesStaticInfoAdapters
    ).pipe(filter((adapters) => adapters.length > 0));

    const MAX_RETRIES = 2;
    return staticInfoAdapters.pipe(
      map((adapters) => this.filterCachedAdapters(adapters)),
      filter((adapters) => adapters.length > 0),
      mergeMap((itineraryAdapters) => {
        const pendingKeys = this.getPendingStaticInfoKeys(itineraryAdapters);

        return searchService.searchOverviewStaticInfo(itineraryAdapters).pipe(
          retryHandled({
            count: MAX_RETRIES,
            delay: 1000,
            onStart: trackItineraryHotelStaticInfoRetriesStarted,
            onSuccess: trackItineraryHotelStaticInfoRetriesSucceeded,
          }),
          catchError((error) => {
            logException(error, {
              context: 'itinerarySearchResults > getStaticInfo',
            });
            return EMPTY;
          }),
          // On the success path where the result is not stale, insertOverviewStaticInfo
          // (called in ItineraryViewModel) clears keys for items the server returned.
          // finalize clears any remaining keys: items not returned by server, stale results,
          // errors, and cancellation.
          finalize(() => this.clearPendingStaticInfoKeys(pendingKeys))
        );
      }),
      share()
    );
  }

  private getPendingStaticInfoKeys(adapters: HotelIngredientAdapter[]): {
    destination: Array<{ code: string; ingredientId: IngredientId }>;
    subIngredients: Array<{ code: string; ingredientId: IngredientId }>;
  } {
    return adapters.reduce<{
      destination: Array<{ code: string; ingredientId: IngredientId }>;
      subIngredients: Array<{ code: string; ingredientId: IngredientId }>;
    }>(
      (allKeys, adapter) => {
        adapter.heartedHotels.forEach((hotel) => {
          allKeys.destination.push({
            code: hotel.code,
            ingredientId: adapter.ingredientId,
          });
        });

        adapter.constraint.hotelCodes?.forEach((code) => {
          if (code) {
            allKeys.destination.push({
              code,
              ingredientId: adapter.ingredientId,
            });
          }
        });

        adapter.subIngredients.forEach((subIngredient) => {
          allKeys.subIngredients.push({
            code: subIngredient.code,
            ingredientId: adapter.ingredientId,
          });
        });

        return allKeys;
      },
      { destination: [], subIngredients: [] }
    );
  }

  private filterCachedAdapters(
    adapters: HotelIngredientAdapter[]
  ): HotelIngredientAdapter[] {
    return adapters.reduce<HotelIngredientAdapter[]>((filtered, adapter) => {
      const uncachedHeartedHotels = adapter.heartedHotels.filter(
        (hotel) =>
          !this.cache.hasDestinationStaticInfo(hotel.code, adapter.ingredientId)
      );

      const uncachedHotelCodes = adapter.constraint.hotelCodes?.filter(
        (code): code is string =>
          !!code &&
          !this.cache.hasDestinationStaticInfo(code, adapter.ingredientId)
      );

      const uncachedSubIngredients = adapter.subIngredients.filter(
        (sub) =>
          !this.cache.hasSubIngredientStaticInfo(sub.code, adapter.ingredientId)
      );

      const hasUncachedData =
        uncachedHeartedHotels.length > 0 ||
        (uncachedHotelCodes?.length ?? 0) > 0 ||
        uncachedSubIngredients.length > 0;

      if (hasUncachedData) {
        uncachedHeartedHotels.forEach((hotel) =>
          this.cache.markDestinationAsPending(hotel.code, adapter.ingredientId)
        );
        uncachedHotelCodes?.forEach((code) =>
          this.cache.markDestinationAsPending(code, adapter.ingredientId)
        );
        uncachedSubIngredients.forEach((sub) =>
          this.cache.markSubIngredientAsPending(sub.code, adapter.ingredientId)
        );

        const constraintOverrides = uncachedHotelCodes
          ? {
              hotelCodes: uncachedHotelCodes,
              maxNumberOfHotels: uncachedHotelCodes.length,
            }
          : {};

        filtered.push({
          ...adapter,
          heartedHotels: uncachedHeartedHotels,
          constraint: {
            ...adapter.constraint,
            ...constraintOverrides,
          },
          subIngredients: uncachedSubIngredients,
        });
      }

      return filtered;
    }, []);
  }

  private clearPendingStaticInfoKeys(keys: {
    destination: Array<{ code: string; ingredientId: IngredientId }>;
    subIngredients: Array<{ code: string; ingredientId: IngredientId }>;
  }): void {
    keys.destination.forEach(({ code, ingredientId }) =>
      this.cache.clearPendingDestination(code, ingredientId)
    );
    keys.subIngredients.forEach(({ code, ingredientId }) =>
      this.cache.clearPendingSubIngredient(code, ingredientId)
    );
  }

  private getRecipeProductCodesStaticInfoAdapters(
    hotelOnlyIngredients: HotelIngredientAdapter[],
    ownStayOnlyIngredients: OwnStayIngredientAdapter[]
  ): HotelIngredientAdapter[] {
    const heartedHotelsByIngredient: HeartedHotelsByIngredient =
      getHeartedHotelCodesByIngredient(hotelOnlyIngredients);

    const activitiesByIngredient: ActivitiesByIngredient =
      getActivityCodesByIngredient(
        [...hotelOnlyIngredients, ...ownStayOnlyIngredients],
        this.uniqueProductCodesByIngredient
      );

    const destinationProductsIngredientIds = uniq([
      ...heartedHotelsByIngredient.keys(),
      ...activitiesByIngredient.keys(),
    ]);

    if (destinationProductsIngredientIds.length === 0) {
      return [];
    }

    return destinationProductsIngredientIds.map((ingredientId) => ({
      constraint: {
        boardBasis: [],
        selectedHotelTypes: [],
      },
      heartedHotels: heartedHotelsByIngredient.get(ingredientId) ?? [],
      subIngredients: activitiesByIngredient.get(ingredientId) ?? [],
      ingredientId,
      ingredientType: IngredientType.Hotel,
      suggestedBy: AvailabilityOrder.None,
    }));

    function getHeartedHotelCodesByIngredient(
      hotelOnlyIngredients: HotelIngredientAdapter[]
    ): HeartedHotelsByIngredient {
      return hotelOnlyIngredients.reduce(
        (heartedHotelsByIngredient, hotelIngredient) => {
          if (hotelIngredient.heartedHotels.length > 0) {
            heartedHotelsByIngredient.set(
              hotelIngredient.ingredientId,
              hotelIngredient.heartedHotels
            );
          }
          return heartedHotelsByIngredient;
        },
        new Map<IngredientId, HeartedHotel[]>()
      );
    }

    function getActivityCodesByIngredient(
      stayOnlyIngredients: (
        | HotelIngredientAdapter
        | OwnStayIngredientAdapter
      )[],
      uniqueProductCodesByIngredient: Map<IngredientId, Set<string>>
    ): ActivitiesByIngredient {
      return stayOnlyIngredients.reduce(
        (activitiesByIngredient, stayIngredient) => {
          if (stayIngredient.subIngredients.length > 0) {
            activitiesByIngredient.set(
              stayIngredient.ingredientId,
              stayIngredient.subIngredients
            );

            const ingredientUniqueCodes = uniqueProductCodesByIngredient.get(
              stayIngredient.ingredientId
            );
            stayIngredient.subIngredients
              .map((subIngredient) => subIngredient.code)
              .forEach((subIngredientCode) =>
                ingredientUniqueCodes?.add(subIngredientCode)
              );
          }

          return activitiesByIngredient;
        },
        new Map<IngredientId, ActivityIngredientAdapter[]>()
      );
    }
  }

  private getRelevantItineraryStaticInfoAdapters(
    stayOnlyIngredients: (HotelIngredientAdapter | OwnStayIngredientAdapter)[],
    selectedDate?: Date
  ): Observable<HotelIngredientAdapter[]> {
    const selectedItinerary = this.itineraries.pipe(
      filter(
        (itinerary) =>
          itinerary.date.toISOString() === selectedDate?.toISOString()
      )
    );

    const relevantItinerary = iif(
      () => isDefined(selectedDate),
      selectedItinerary,
      this.cheapestItinerary
    );

    return this.getItinerariesStaticInfoAdapters(
      relevantItinerary,
      stayOnlyIngredients,
      { yieldEveryItinerary: true }
    );
  }

  private getItinerariesStaticInfoAdapters(
    itineraries: Observable<ItineraryWithPrice>,
    stayOnlyIngredients: (HotelIngredientAdapter | OwnStayIngredientAdapter)[],
    options?: {
      yieldEveryItinerary?: boolean;
    }
  ): Observable<HotelIngredientAdapter[]> {
    const itinerariesProductCodes: Observable<ProductCodesByIngredient> =
      itineraries.pipe(
        map((itinerary) =>
          this.getItineraryProductCodes([
            ...itinerary.hotelOnlyItinerary,
            ...itinerary.ownStayOnlyItinerary,
          ])
        ),
        filter((itineraryProductCodes) => itineraryProductCodes.size > 0)
      );

    return iif(
      () => !!options?.yieldEveryItinerary,
      itinerariesProductCodes.pipe(bufferCount(1)),
      itinerariesProductCodes.pipe(toArray())
    ).pipe(
      map((bufferedItineraryProductCodes) =>
        bufferedItineraryProductCodes.reduce(
          (
            totalItinerariesProductCodes: ProductCodesByIngredient,
            itineraryProductCodes
          ) => {
            Array.from(itineraryProductCodes.entries()).forEach(
              ([ingredientId, { activityCodes, hotelCodes }]) => {
                const currentIngredientProductCodes =
                  totalItinerariesProductCodes.get(ingredientId);

                currentIngredientProductCodes?.activityCodes.push(
                  ...activityCodes
                );
                currentIngredientProductCodes?.hotelCodes.push(...hotelCodes);
              }
            );

            return totalItinerariesProductCodes;
          },
          new Map<IngredientId, IngredientProductCodes>(
            stayOnlyIngredients.map<[IngredientId, IngredientProductCodes]>(
              (ingredient) => [
                ingredient.ingredientId,
                { hotelCodes: [], activityCodes: [] },
              ]
            )
          )
        )
      ),
      map((totalProductCodes) =>
        Array.from(totalProductCodes.entries()).reduce(
          (
            adapters: HotelIngredientAdapter[],
            [ingredientId, { activityCodes, hotelCodes }]
          ) => {
            if (activityCodes.length + hotelCodes.length > 0) {
              adapters.push(
                this.createHotelIngredientAdapter(
                  stayOnlyIngredients,
                  hotelCodes,
                  activityCodes,
                  ingredientId
                )
              );
            }

            return adapters;
          },
          []
        )
      ),
      share()
    );
  }

  private getItineraryProductCodes(
    itinerary: ItineraryMainIngredientResponse[]
  ): ProductCodesByIngredient {
    return itinerary.reduce((productCodesByIngredient, ingredientResponse) => {
      const hotelCodes: string[] = isHotelIngredientResponse(ingredientResponse)
        ? this.getHotelIngredientResponseCodes(ingredientResponse)
        : [];
      const activityCodes: string[] =
        isHotelIngredientResponse(ingredientResponse) ||
        isOwnStayIngredientResponse(ingredientResponse)
          ? this.getStayIngredientResponseActivityCodes(ingredientResponse)
          : [];

      if (hotelCodes.length || activityCodes.length) {
        productCodesByIngredient.set(ingredientResponse.ingredientId, {
          hotelCodes,
          activityCodes,
        });
      }

      return productCodesByIngredient;
    }, new Map<IngredientId, IngredientProductCodes>());
  }

  private getHotelIngredientResponseCodes(
    ingredientResponse: HotelIngredientResponse
  ): string[] {
    const ingredientUniqueCodes = this.uniqueProductCodesByIngredient.get(
      ingredientResponse.ingredientId
    );

    const hotelCodes: string[] = [];

    if (isAvailableHotelIngredientResponse(ingredientResponse)) {
      const suggestedHotelProductCode = ingredientResponse.suggested.code;

      this.addUniqueCode(
        suggestedHotelProductCode,
        hotelCodes,
        ingredientUniqueCodes
      );

      ingredientResponse.suggestions.forEach((suggestion) => {
        const suggestionHotelProductCode = suggestion.code;

        this.addUniqueCode(
          suggestionHotelProductCode,
          hotelCodes,
          ingredientUniqueCodes
        );
      });
    } else {
      if (ingredientResponse.alternativeSuggestion) {
        this.addUniqueCode(
          ingredientResponse.alternativeSuggestion.code,
          hotelCodes,
          ingredientUniqueCodes
        );
      }

      ingredientResponse.suggestions.forEach((suggestion) => {
        this.addUniqueCode(suggestion.code, hotelCodes, ingredientUniqueCodes);
      });
    }

    return hotelCodes;
  }

  private getStayIngredientResponseActivityCodes(
    ingredientResponse: HotelIngredientResponse | OwnStayIngredientResponse
  ): string[] {
    const ingredientUniqueCodes = this.uniqueProductCodesByIngredient.get(
      ingredientResponse.ingredientId
    );

    const activityCodes: string[] = [];

    ingredientResponse.subIngredientResponse.forEach(
      (activitySubIngredient) => {
        if (isAvailableActivityIngredientResponse(activitySubIngredient)) {
          const suggestedActivityCode = activitySubIngredient.suggested.code;

          this.addUniqueCode(
            suggestedActivityCode,
            activityCodes,
            ingredientUniqueCodes
          );
        } else if (activitySubIngredient.alternativeSuggestion) {
          const alternativeSuggestedActivityCode =
            activitySubIngredient.alternativeSuggestion.code;

          this.addUniqueCode(
            alternativeSuggestedActivityCode,
            activityCodes,
            ingredientUniqueCodes
          );
        }
      }
    );

    return activityCodes;
  }

  private addUniqueCode(
    code: string,
    ingredientCodes: string[],
    ingredientUniqueCodes?: Set<string>
  ): void {
    if (!ingredientUniqueCodes?.has(code)) {
      ingredientCodes.push(code);
      ingredientUniqueCodes?.add(code);
    }
  }

  private createHotelIngredientAdapter(
    stayOnlyIngredients: (HotelIngredientAdapter | OwnStayIngredientAdapter)[],
    hotelCodes: string[],
    activityCodes: string[],
    ingredientId: IngredientId
  ): HotelIngredientAdapter {
    const currentStayIngredient = stayOnlyIngredients.find(
      (stay) => stay.ingredientId === ingredientId
    );

    if (!currentStayIngredient) {
      throw new Error('HotelIngredient criteria not found for specified');
    }

    return {
      ingredientId: currentStayIngredient.ingredientId,
      ingredientType: IngredientType.Hotel,
      suggestedBy: AvailabilityOrder.Popular,
      constraint: {
        boardBasis: [],
        selectedHotelTypes: [],
        hotelCodes,
        maxNumberOfHotels: hotelCodes.length,
      },
      subIngredients: activityCodes.map((activityCode) => ({
        code: activityCode,
        ingredientType: IngredientType.Activity,
        offset: 0,
        ingredientId: currentStayIngredient.ingredientId,
        tickets: [],
      })),
      heartedHotels: [],
    };
  }
}
